import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { parseFootageNumber } from "./expansionGroupUtils";
import { readSubsegmentSummaryFromAcf } from "./expansionProjectAcf";
import { resolveReportInspectorKey } from "./inspectorReportCalendarUtils";
import {
  classifyDailyProgressReportSource,
  resolveReportConstructionLeadKey,
} from "./crewReportCalendarUtils";

/** Local calendar YYYY-MM-DD (not UTC — toISOString shifts the day in US evenings). */
export function todayIsoDate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isPlainObject(v) {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

/** Daily progress log rows stored on the construction domain. */
export function readDailyProgressReportsFromAcf(acfGroupsJson) {
  const j = isPlainObject(acfGroupsJson) ? acfGroupsJson : {};
  const cons =
    j[COMMERCIAL_DOMAINS.CONSTRUCTION] ??
    j.construction_details ??
    j["Construction Details"] ??
    {};
  const raw = cons.daily_progress_reports;
  if (Array.isArray(raw)) return raw;
  if (!isPlainObject(raw)) return [];
  return Object.keys(raw)
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => raw[k]);
}

function sumDailyReportTotals(reports) {
  let conduit = 0;
  let fiber = 0;
  let proofing = 0;
  let vaults = 0;
  for (const row of reports) {
    conduit += parseFootageNumber(row?.conduit_footage) ?? 0;
    fiber += parseFootageNumber(row?.fiber_footage) ?? 0;
    proofing += parseFootageNumber(row?.proofing_footage) ?? 0;
    vaults += parseFootageNumber(row?.vaults) ?? 0;
  }
  return { conduit, fiber, proofing, vaults };
}

/** Normalize legacy completed_footage into split conduit/fiber totals. */
export function normalizeSubsegmentFootage(subSummary) {
  const sub = { ...(subSummary && typeof subSummary === "object" ? subSummary : {}) };
  let conduit = parseFootageNumber(sub.completed_conduit_footage);
  let fiber = parseFootageNumber(sub.completed_fiber_footage);
  const proofing = parseFootageNumber(sub.completed_proofing_footage);
  const legacy = parseFootageNumber(sub.completed_footage);

  if (conduit == null && fiber == null && legacy != null) {
    conduit = legacy;
    fiber = 0;
  } else if (conduit == null && fiber != null) {
    if (legacy != null && legacy >= fiber) {
      conduit = legacy - fiber;
    } else {
      conduit = 0;
    }
  } else if (conduit != null && fiber == null && legacy != null && legacy > conduit) {
    fiber = legacy - conduit;
  }

  const conduitTotal = conduit ?? 0;
  const fiberTotal = fiber ?? 0;
  sub.completed_conduit_footage = conduitTotal;
  sub.completed_fiber_footage = fiberTotal;
  sub.completed_proofing_footage = proofing ?? 0;
  sub.completed_footage = conduitTotal + fiberTotal;
  return sub;
}

/** Merge subsegment cumulative fields with summed daily report rows. */
export function resolveInstallationTotals(subSummary, dailyReports) {
  const sub = normalizeSubsegmentFootage(subSummary);
  const reports = Array.isArray(dailyReports) ? dailyReports : [];
  if (!reports.length) return sub;

  const sums = sumDailyReportTotals(reports);
  return syncCompletedFootageTotals({
    ...sub,
    completed_conduit_footage: Math.max(
      parseFootageNumber(sub.completed_conduit_footage) ?? 0,
      sums.conduit
    ),
    completed_fiber_footage: Math.max(
      parseFootageNumber(sub.completed_fiber_footage) ?? 0,
      sums.fiber
    ),
    completed_proofing_footage: Math.max(
      parseFootageNumber(sub.completed_proofing_footage) ?? 0,
      sums.proofing
    ),
    handholes: Math.max(parseFootageNumber(sub.handholes) ?? 0, sums.vaults),
  });
}

/** Fiber install target: override footage, else construction footage. */
export function fiberFootageDenominator(subSummary) {
  const sub = normalizeSubsegmentFootage(subSummary);
  const override = parseFootageNumber(sub.override_footage);
  if (override != null && override > 0) return override;
  const construction = parseFootageNumber(sub.construction_footage);
  if (construction != null && construction > 0) return construction;
  return null;
}

/** Conduit install target: construction footage only. */
export function conduitFootageDenominator(subSummary) {
  const sub = normalizeSubsegmentFootage(subSummary);
  const construction = parseFootageNumber(sub.construction_footage);
  if (construction != null && construction > 0) return construction;
  return null;
}

/** @deprecated Use fiberFootageDenominator */
export function footageDenominator(subSummary) {
  return fiberFootageDenominator(subSummary);
}

export function computeFootageProgressForProject(projectRow) {
  const sub = readSubsegmentSummaryFromAcf(projectRow?.acf_groups_json);
  const reports = readDailyProgressReportsFromAcf(projectRow?.acf_groups_json);
  const phase = sub?.phase ?? projectRow?.phase_label ?? "";
  return computeFootageProgress(sub, reports, { phase });
}

function isAccountingPhase(phase) {
  return String(phase || "")
    .trim()
    .toLowerCase() === "accounting";
}

/** Overall install %: average conduit and fiber when both have targets. */
export function computeInstallationPercentComplete({
  conduitPct = 0,
  fiberPct = 0,
  conduitDenominator = null,
  fiberDenominator = null,
}) {
  const parts = [];
  if (conduitDenominator != null && conduitDenominator > 0) {
    parts.push(Math.min(100, conduitPct));
  }
  if (fiberDenominator != null && fiberDenominator > 0) {
    parts.push(Math.min(100, fiberPct));
  }
  if (parts.length === 0) return null;
  return Math.min(100, Math.round(parts.reduce((sum, pct) => sum + pct, 0) / parts.length));
}

export function computeFootageProgress(subSummary, dailyReports, options = {}) {
  const sub = resolveInstallationTotals(subSummary, dailyReports);
  const fiberDenominator = fiberFootageDenominator(sub);
  const conduitDenominator = conduitFootageDenominator(sub);
  let conduitCompleted = parseFootageNumber(sub.completed_conduit_footage) ?? 0;
  let fiberCompleted = parseFootageNumber(sub.completed_fiber_footage) ?? 0;
  const proofingCompleted = parseFootageNumber(sub.completed_proofing_footage) ?? 0;
  const vaults = parseFootageNumber(sub.handholes) ?? 0;
  const phase = options.phase ?? sub?.phase ?? "";
  const inAccounting = isAccountingPhase(phase);
  const override = parseFootageNumber(sub.override_footage);
  const hasOverrideFiberTarget = override != null && override > 0;

  // No construction target → ignore leftover conduit totals (fiber-only / override jobs).
  if (conduitDenominator == null || conduitDenominator <= 0) {
    conduitCompleted = 0;
  } else {
    conduitCompleted = Math.min(conduitCompleted, conduitDenominator);
  }

  if (fiberDenominator != null && fiberDenominator > 0) {
    fiberCompleted = Math.min(fiberCompleted, fiberDenominator);
  }

  // New-construction only: fiber cannot exceed installed conduit.
  // Override fiber-only targets must not be capped by stray conduit values.
  if (!hasOverrideFiberTarget && conduitCompleted > 0) {
    fiberCompleted = Math.min(fiberCompleted, conduitCompleted);
  }

  if (inAccounting && fiberDenominator != null && fiberDenominator > 0) {
    fiberCompleted = Math.max(fiberCompleted, fiberDenominator);
    if (!hasOverrideFiberTarget && conduitCompleted > 0) {
      fiberCompleted = Math.min(fiberCompleted, conduitCompleted);
    }
  }

  const completedTotal = conduitCompleted + fiberCompleted;

  const conduitPct =
    conduitDenominator != null && conduitDenominator > 0
      ? Math.min(100, (conduitCompleted / conduitDenominator) * 100)
      : 0;
  const fiberPct =
    inAccounting && fiberDenominator != null && fiberDenominator > 0
      ? 100
      : fiberDenominator != null && fiberDenominator > 0
        ? Math.min(100, (fiberCompleted / fiberDenominator) * 100)
        : 0;
  const percentComplete = computeInstallationPercentComplete({
    conduitPct,
    fiberPct,
    conduitDenominator,
    fiberDenominator,
  });

  return {
    denominator: fiberDenominator,
    fiberDenominator,
    conduitDenominator,
    conduitCompleted,
    fiberCompleted,
    proofingCompleted,
    completedTotal,
    vaults,
    percentComplete,
    conduitPct,
    fiberPct,
    inAccounting,
  };
}

export function syncCompletedFootageTotals(subSummary) {
  const sub = normalizeSubsegmentFootage(subSummary);
  sub.completed_footage =
    (parseFootageNumber(sub.completed_conduit_footage) ?? 0) +
    (parseFootageNumber(sub.completed_fiber_footage) ?? 0);
  return sub;
}

/**
 * Apply a daily inspector report: increment cumulative totals and append a log row.
 */
export function applyDailyProgressReport(domains, report) {
  const sub = syncCompletedFootageTotals({ ...(domains.subsegment_summary || {}) });
  const cons = { ...(domains[COMMERCIAL_DOMAINS.CONSTRUCTION] || {}) };

  const conduitAdd = parseFootageNumber(report.conduitFootage) ?? 0;
  const fiberAdd = parseFootageNumber(report.fiberFootage) ?? 0;
  const proofingAdd = parseFootageNumber(report.proofingFootage) ?? 0;
  const vaultsAdd = parseFootageNumber(report.vaults) ?? 0;
  const notes = report.notes != null ? String(report.notes).trim() : "";
  const contractor = report.contractor != null ? String(report.contractor).trim() : "";
  const date = report.date || todayIsoDate();

  if (conduitAdd > 0) {
    sub.completed_conduit_footage =
      (parseFootageNumber(sub.completed_conduit_footage) ?? 0) + conduitAdd;
  }
  if (fiberAdd > 0) {
    sub.completed_fiber_footage =
      (parseFootageNumber(sub.completed_fiber_footage) ?? 0) + fiberAdd;
  }
  if (proofingAdd > 0) {
    sub.completed_proofing_footage =
      (parseFootageNumber(sub.completed_proofing_footage) ?? 0) + proofingAdd;
  }
  syncCompletedFootageTotals(sub);

  if (vaultsAdd > 0) {
    sub.handholes = (parseFootageNumber(sub.handholes) ?? 0) + vaultsAdd;
  }

  const reports = Array.isArray(cons.daily_progress_reports)
    ? [...cons.daily_progress_reports]
    : [];
  reports.push({
    date,
    conduit_footage: conduitAdd > 0 ? conduitAdd : null,
    fiber_footage: fiberAdd > 0 ? fiberAdd : null,
    proofing_footage: proofingAdd > 0 ? proofingAdd : null,
    vaults: vaultsAdd > 0 ? vaultsAdd : null,
    notes,
    contractor: contractor || null,
    reported_by: report.reporterEmail != null ? String(report.reporterEmail).trim() : "",
    report_source: report.reportSource === "crew" ? "crew" : "inspector",
  });
  cons.daily_progress_reports = reports;
  cons.reported_footage_date = date;

  return {
    ...domains,
    subsegment_summary: resolveInstallationTotals(sub, reports),
    [COMMERCIAL_DOMAINS.CONSTRUCTION]: cons,
  };
}

/** Daily inspector report for commercial projects (totals on construction domain). */
export function applyCommercialDailyProgressReport(domains, report) {
  const cons = { ...(domains[COMMERCIAL_DOMAINS.CONSTRUCTION] || {}) };

  const conduitAdd = parseFootageNumber(report.conduitFootage) ?? 0;
  const fiberAdd = parseFootageNumber(report.fiberFootage) ?? 0;
  const proofingAdd = parseFootageNumber(report.proofingFootage) ?? 0;
  const vaultsAdd = parseFootageNumber(report.vaults) ?? 0;
  const notes = report.notes != null ? String(report.notes).trim() : "";
  const contractor = report.contractor != null ? String(report.contractor).trim() : "";
  const date = report.date || todayIsoDate();

  if (conduitAdd > 0) {
    cons.completed_conduit_footage =
      (parseFootageNumber(cons.completed_conduit_footage) ?? 0) + conduitAdd;
  }
  if (fiberAdd > 0) {
    cons.completed_fiber_footage =
      (parseFootageNumber(cons.completed_fiber_footage) ?? 0) + fiberAdd;
  }
  if (proofingAdd > 0) {
    cons.completed_proofing_footage =
      (parseFootageNumber(cons.completed_proofing_footage) ?? 0) + proofingAdd;
  }
  if (vaultsAdd > 0) {
    cons.handholes = (parseFootageNumber(cons.handholes) ?? 0) + vaultsAdd;
  }

  const reports = Array.isArray(cons.daily_progress_reports)
    ? [...cons.daily_progress_reports]
    : [];
  reports.push({
    date,
    conduit_footage: conduitAdd > 0 ? conduitAdd : null,
    fiber_footage: fiberAdd > 0 ? fiberAdd : null,
    proofing_footage: proofingAdd > 0 ? proofingAdd : null,
    vaults: vaultsAdd > 0 ? vaultsAdd : null,
    notes,
    contractor: contractor || null,
    reported_by: report.reporterEmail != null ? String(report.reporterEmail).trim() : "",
    report_source: report.reportSource === "crew" ? "crew" : "inspector",
  });
  cons.daily_progress_reports = reports;
  cons.reported_footage_date = date;

  return {
    ...domains,
    [COMMERCIAL_DOMAINS.CONSTRUCTION]: cons,
  };
}

export function dailyProgressHasContent(report) {
  const conduit = parseFootageNumber(report?.conduitFootage);
  const fiber = parseFootageNumber(report?.fiberFootage);
  const proofing = parseFootageNumber(report?.proofingFootage);
  const vaults = parseFootageNumber(report?.vaults);
  const notes = report?.notes != null ? String(report.notes).trim() : "";
  return (
    (conduit != null && conduit > 0) ||
    (fiber != null && fiber > 0) ||
    (proofing != null && proofing > 0) ||
    (vaults != null && vaults > 0) ||
    notes.length > 0
  );
}

export function formatFootageNumber(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

export function inspectorReportRowHasContent(report) {
  const conduit = parseFootageNumber(report?.conduit_footage);
  const fiber = parseFootageNumber(report?.fiber_footage);
  const proofing = parseFootageNumber(report?.proofing_footage);
  const vaults = parseFootageNumber(report?.vaults);
  const notes = report?.notes != null ? String(report.notes).trim() : "";
  return (
    (conduit != null && conduit > 0) ||
    (fiber != null && fiber > 0) ||
    (proofing != null && proofing > 0) ||
    (vaults != null && vaults > 0) ||
    notes.length > 0
  );
}

/** True when a daily progress report includes conduit, fiber, proofing, or vault units. */
export function inspectorReportHasUnits(report) {
  const conduit = parseFootageNumber(report?.conduit_footage);
  const fiber = parseFootageNumber(report?.fiber_footage);
  const proofing = parseFootageNumber(report?.proofing_footage);
  const vaults = parseFootageNumber(report?.vaults);
  return (
    (conduit != null && conduit > 0) ||
    (fiber != null && fiber > 0) ||
    (proofing != null && proofing > 0) ||
    (vaults != null && vaults > 0)
  );
}

/**
 * Earliest calendar date an inspector reported units on this subsegment.
 * Used as the subsegment "started" date for New Feet reporting.
 */
export function firstInspectorUnitsReportDate(projectRow) {
  const reports = readDailyProgressReportsFromAcf(projectRow?.acf_groups_json);
  let earliest = null;
  for (const report of reports) {
    if (!inspectorReportHasUnits(report)) continue;
    const day = parseCalendarDateOnly(report?.date);
    if (!day) continue;
    if (!earliest || day < earliest) earliest = day;
  }
  return earliest;
}

/**
 * Sum conduit/fiber/proofing footage from inspector daily progress reports, optionally
 * limited to reports whose date falls in [from, to].
 */
export function sumInspectorReportedFootage(projectRow, bounds = {}) {
  const from = bounds?.from || null;
  const to = bounds?.to || null;
  const reports = readDailyProgressReportsFromAcf(projectRow?.acf_groups_json);
  let conduitFootage = 0;
  let fiberFootage = 0;
  let proofingFootage = 0;
  let hasConduit = false;
  let hasFiber = false;
  let hasProofing = false;

  for (const report of reports) {
    const day = parseCalendarDateOnly(report?.date);
    if (!day) continue;
    if (from && day < from) continue;
    if (to && day > to) continue;

    const conduit = parseFootageNumber(report?.conduit_footage);
    const fiber = parseFootageNumber(report?.fiber_footage);
    const proofing = parseFootageNumber(report?.proofing_footage);
    if (conduit != null && conduit > 0) {
      conduitFootage += conduit;
      hasConduit = true;
    }
    if (fiber != null && fiber > 0) {
      fiberFootage += fiber;
      hasFiber = true;
    }
    if (proofing != null && proofing > 0) {
      proofingFootage += proofing;
      hasProofing = true;
    }
  }

  return {
    conduitFootage: hasConduit ? Math.round(conduitFootage * 100) / 100 : 0,
    fiberFootage: hasFiber ? Math.round(fiberFootage * 100) / 100 : 0,
    proofingFootage: hasProofing ? Math.round(proofingFootage * 100) / 100 : 0,
    hasConduit,
    hasFiber,
    hasProofing,
  };
}

export function formatInspectorReportDetail(report) {
  const parts = [];
  const contractor = report?.contractor != null ? String(report.contractor).trim() : "";
  const conduit = parseFootageNumber(report?.conduit_footage);
  const fiber = parseFootageNumber(report?.fiber_footage);
  const proofing = parseFootageNumber(report?.proofing_footage);
  const vaults = parseFootageNumber(report?.vaults);
  if (conduit != null && conduit > 0) {
    parts.push(`Conduit: ${formatFootageNumber(conduit)} ft`);
  }
  if (fiber != null && fiber > 0) {
    parts.push(`Fiber: ${formatFootageNumber(fiber)} ft`);
  }
  if (proofing != null && proofing > 0) {
    parts.push(`Proofing: ${formatFootageNumber(proofing)} ft`);
  }
  if (vaults != null && vaults > 0) {
    parts.push(`Vaults: ${formatFootageNumber(vaults)}`);
  }
  if (contractor) {
    parts.push(`Contractor: ${contractor}`);
  }
  const notes = report?.notes != null ? String(report.notes).trim() : "";
  if (notes) parts.push(notes);
  return parts.join(" · ");
}

function parseCalendarDateOnly(value) {
  if (value == null || String(value).trim() === "") return null;
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Build single-day calendar events from daily progress reports on an expansion row.
 * Construction Lead submissions become crewReports; others become inspectorReports.
 */
export function buildDailyProgressReportCalendarEventsForRow(
  row,
  rangeStart,
  rangeEnd,
  { inspectorMeta = {}, crewMeta = {} } = {}
) {
  const reports = readDailyProgressReportsFromAcf(row?.acf_groups_json);
  if (!reports.length) return [];

  const sub = readSubsegmentSummaryFromAcf(row?.acf_groups_json);
  const segment = sub.segment != null ? String(sub.segment).trim() : "";
  const workOrder = sub.work_order ?? row.work_order ?? "—";
  const customer = sub.project_name ?? row.name ?? row.customer ?? "—";
  const projectId = row.id;
  const events = [];

  reports.forEach((report, reportIndex) => {
    if (!inspectorReportRowHasContent(report)) return;
    const day = parseCalendarDateOnly(report?.date);
    if (!day) return;
    if (day < rangeStart || day > rangeEnd) return;

    const source = classifyDailyProgressReportSource(report);
    if (source === "crew") {
      events.push({
        eventType: "crewReports",
        eventKey: `crew-report-${reportIndex}`,
        projectId,
        workOrder,
        customer,
        segment,
        start: day,
        end: day,
        color: crewMeta.color || "#0d9488",
        typeLabel: crewMeta.label || "Crew Reports",
        detail: formatInspectorReportDetail(report),
        report,
        reportIndex,
        constructionLeadKey: resolveReportConstructionLeadKey(report),
      });
      return;
    }

    events.push({
      eventType: "inspectorReports",
      eventKey: `report-${reportIndex}`,
      projectId,
      workOrder,
      customer,
      segment,
      start: day,
      end: day,
      color: inspectorMeta.color || "#d97706",
      typeLabel: inspectorMeta.label || "Inspector Reports",
      detail: formatInspectorReportDetail(report),
      report,
      reportIndex,
      inspectorKey: resolveReportInspectorKey(report, row),
    });
  });

  return events;
}

/**
 * @deprecated Prefer buildDailyProgressReportCalendarEventsForRow — kept for callers that only want inspector chips.
 */
export function buildInspectorReportCalendarEventsForRow(
  row,
  rangeStart,
  rangeEnd,
  eventMeta = {}
) {
  return buildDailyProgressReportCalendarEventsForRow(row, rangeStart, rangeEnd, {
    inspectorMeta: eventMeta,
    crewMeta: {},
  }).filter((ev) => ev.eventType === "inspectorReports");
}
